import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, receiptUrl } from "@/lib/api";
import { SCHEMES, SCHEME_LIST, COMMON_SECTIONS } from "@/lib/schemes";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Loader2, Upload, FileCheck2, X, CheckCircle2, Download, Printer, LayoutDashboard, PartyPopper } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function ApplicationForm() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialScheme = params.get("scheme");
  const [schemeCode, setSchemeCode] = useState(SCHEMES[initialScheme] ? initialScheme : "");
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState("");
  const [submitted, setSubmitted] = useState(null);
  const [declAccepted, setDeclAccepted] = useState(false);
  const [data, setData] = useState({ applicant: {}, address: {}, bank: {}, scheme_data: {}, documents: {} });

  const scheme = SCHEMES[schemeCode];
  const steps = scheme ? [...COMMON_SECTIONS, scheme.section, { id: "documents", title: "Documents" }, { id: "review", title: "Review & Declaration" }] : [];

  const set = (sec, key, val) => setData((d) => ({ ...d, [sec]: { ...d[sec], [key]: val } }));

  const uploadDoc = async (docKey, file) => {
    if (!file) return;
    setUploading(docKey);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("documents", docKey, { file_id: res.data.id, filename: res.data.filename, upload_date: today() });
      toast.success(`${file.name} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading("");
    }
  };

  const removeDoc = (k) => setData((d) => { const x = { ...d.documents }; delete x[k]; return { ...d, documents: x }; });

  const sectionValid = (sec) => {
    if (sec.id === "documents") return scheme.documents.filter((x) => x.required).every((x) => data.documents[x.key]);
    if (!sec.fields) return true;
    return sec.fields.filter((f) => f.required).every((f) => data[sec.id]?.[f.key]);
  };

  const next = () => {
    const s = steps[step];
    if (s.fields || s.id === "documents") {
      if (!sectionValid(s)) { toast.error("Please fill all required (*) items on this step"); return; }
    }
    setStep((x) => Math.min(x + 1, steps.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => { setStep((x) => Math.max(x - 1, 0)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async () => {
    if (!declAccepted) { toast.error("Please accept the declaration to submit"); return; }
    setSubmitting(true);
    try {
      const payload = {
        scheme: schemeCode,
        applicant: data.applicant, address: data.address, bank: data.bank,
        scheme_data: data.scheme_data, documents: data.documents,
        document_list: scheme.documents.map((d) => ({ key: d.key, label: d.label })),
        declaration_accepted: true, declaration_name: data.applicant.full_name || "",
      };
      const res = await api.post("/applications", payload);
      setSubmitted(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Success screen ----
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#f8faf8]">
        <Header />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
          <Card className="rounded-3xl border-emerald-100 p-8 sm:p-10 text-center shadow-sm" data-testid="success-screen">
            <div className="h-16 w-16 mx-auto rounded-2xl bg-emerald-50 grid place-items-center"><PartyPopper className="h-8 w-8 text-emerald-600" /></div>
            <h1 className="mt-5 font-head font-extrabold text-2xl sm:text-3xl text-emerald-950">Application Submitted Successfully</h1>
            <p className="mt-2 text-slate-500">Your application has been submitted. Please save your Application Number for future reference.</p>
            <div className="mt-6 rounded-2xl bg-emerald-50 border border-emerald-100 p-5">
              <div className="text-xs uppercase tracking-wide text-slate-400">Application Number</div>
              <div className="font-head font-extrabold text-2xl text-emerald-800 mt-1" data-testid="success-number">{submitted.number}</div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm text-left">
                <div><span className="text-slate-400">Scheme</span><div className="font-medium text-slate-700">{submitted.scheme_name}</div></div>
                <div><span className="text-slate-400">Status</span><div className="font-medium text-emerald-700">Submitted</div></div>
                <div><span className="text-slate-400">Date</span><div className="font-medium text-slate-700">{new Date(submitted.created_at).toLocaleDateString("en-IN")}</div></div>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <a href={receiptUrl(submitted.id)} target="_blank" rel="noreferrer" data-testid="success-download-pdf"><Button className="rounded-full bg-brand hover:bg-emerald-800 text-white"><Download className="h-4 w-4 mr-1.5" /> Download Application PDF</Button></a>
              <Button variant="outline" className="rounded-full border-emerald-200 text-emerald-700" onClick={() => window.open(receiptUrl(submitted.id), "_blank")} data-testid="success-print"><Printer className="h-4 w-4 mr-1.5" /> Print Application</Button>
              <Button variant="outline" className="rounded-full" onClick={() => navigate("/dashboard")} data-testid="success-track"><LayoutDashboard className="h-4 w-4 mr-1.5" /> Track / Go to Dashboard</Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // ---- Scheme picker ----
  if (!scheme) {
    return (
      <div className="min-h-screen bg-[#f8faf8]">
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
          <h1 className="font-head font-extrabold text-2xl sm:text-3xl text-emerald-950">Choose a Scheme to Apply</h1>
          <p className="mt-1.5 text-slate-500">Select the government scheme you want to apply for.</p>
          <div className="mt-8 grid sm:grid-cols-2 gap-5">
            {SCHEME_LIST.map((s) => (
              <button key={s.code} onClick={() => { setSchemeCode(s.code); setStep(0); }} data-testid={`pick-scheme-${s.code}`} className="text-left bg-white rounded-3xl p-6 border border-emerald-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all">
                <div className="h-12 w-12 rounded-2xl grid place-items-center" style={{ backgroundColor: s.tint }}><s.icon className="h-6 w-6" style={{ color: s.color }} /></div>
                <h3 className="mt-4 font-head font-bold text-emerald-950">{s.name}</h3>
                <p className="mt-1.5 text-sm text-slate-500">{s.short}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-emerald-700 font-semibold text-sm">Apply <ArrowRight className="h-4 w-4" /></span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const cur = steps[step];
  const progress = ((step + 1) / steps.length) * 100;

  return (
    <div className="min-h-screen bg-[#f8faf8]">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 lg:py-10">
        <div className="mb-6">
          <button onClick={() => setSchemeCode("")} className="text-sm text-emerald-700 hover:underline flex items-center gap-1" data-testid="change-scheme"><ArrowLeft className="h-4 w-4" /> Change scheme</button>
          <h1 className="mt-2 font-head font-extrabold text-xl sm:text-2xl text-emerald-950">{scheme.name}</h1>
          <p className="mt-1 text-sm text-slate-500">Step {step + 1} of {steps.length}: {cur.title}</p>
          <Progress value={progress} className="mt-4 h-2 bg-emerald-100" />
        </div>

        <Card className="rounded-3xl border-emerald-100 p-6 sm:p-8 shadow-sm">
          {cur.fields && (
            <div className="grid sm:grid-cols-2 gap-x-5 gap-y-4">
              {cur.fields.map((f) => (
                <FieldRenderer key={f.key} f={f} value={data[cur.id]?.[f.key] || ""} onChange={(v) => set(cur.id, f.key, v)} />
              ))}
            </div>
          )}

          {cur.id === "documents" && (
            <div className="grid sm:grid-cols-2 gap-4">
              {scheme.documents.map((d) => {
                const up = data.documents[d.key];
                return (
                  <div key={d.key} className={`rounded-2xl border p-4 ${up ? "border-emerald-300 bg-emerald-50/50" : "border-dashed border-slate-300"}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">{d.label} {d.required && <span className="text-rose-500">*</span>}</span>
                      {up && <button onClick={() => removeDoc(d.key)} className="text-slate-400 hover:text-rose-500" data-testid={`remove-${d.key}`}><X className="h-4 w-4" /></button>}
                    </div>
                    {up ? (
                      <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700"><FileCheck2 className="h-4 w-4" /> <span className="truncate">{up.filename}</span></div>
                    ) : (
                      <label className="mt-3 flex items-center justify-center gap-2 h-10 rounded-xl bg-white border border-slate-200 text-sm text-slate-500 cursor-pointer hover:border-emerald-400 hover:text-emerald-700" data-testid={`upload-${d.key}`}>
                        {uploading === d.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4" /> Choose file</>}
                        <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadDoc(d.key, e.target.files[0])} disabled={uploading === d.key} />
                      </label>
                    )}
                  </div>
                );
              })}
              <p className="sm:col-span-2 text-xs text-slate-400">Images and PDF up to 8MB each. Required documents marked *.</p>
            </div>
          )}

          {cur.id === "review" && (
            <div className="space-y-6">
              <ReviewBlock title="Applicant" data={data.applicant} fields={COMMON_SECTIONS[0].fields} />
              <ReviewBlock title="Address" data={data.address} fields={COMMON_SECTIONS[1].fields} />
              <ReviewBlock title="Bank" data={data.bank} fields={COMMON_SECTIONS[2].fields} />
              <ReviewBlock title={scheme.section.title} data={data.scheme_data} fields={scheme.section.fields} />
              <div>
                <h4 className="font-head font-semibold text-emerald-950 mb-2 text-sm uppercase tracking-wide">Documents</h4>
                <div className="flex flex-wrap gap-2">
                  {scheme.documents.map((d) => (
                    <span key={d.key} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${data.documents[d.key] ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-50 border-slate-200 text-slate-400"}`}>
                      {data.documents[d.key] ? <FileCheck2 className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />} {d.label}
                    </span>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4">
                <div className="flex items-start gap-3">
                  <Checkbox checked={declAccepted} onCheckedChange={setDeclAccepted} className="mt-0.5" data-testid="declaration-checkbox" />
                  <label className="text-sm text-emerald-900 cursor-pointer" onClick={() => setDeclAccepted(!declAccepted)}>
                    I hereby declare that the information provided by me is true and correct to the best of my knowledge. I understand that submission of this application does not guarantee approval and that the application will be subject to verification and the applicable scheme rules.
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <Button variant="ghost" onClick={back} disabled={step === 0} className="rounded-full text-slate-500" data-testid="app-back-button"><ArrowLeft className="mr-1.5 h-4 w-4" /> Back</Button>
            {step < steps.length - 1 ? (
              <Button onClick={next} className="rounded-full bg-brand hover:bg-emerald-800 text-white px-6" data-testid="app-next-button">Continue <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
            ) : (
              <Button onClick={submit} disabled={submitting} className="rounded-full bg-brand hover:bg-emerald-800 text-white px-6" data-testid="app-submit-button">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Submit Application <CheckCircle2 className="ml-1.5 h-4 w-4" /></>}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

const FieldRenderer = ({ f, value, onChange }) => (
  <div className={f.full ? "sm:col-span-2" : ""}>
    <Label className="text-slate-600 text-sm">{f.label} {f.required && <span className="text-rose-500">*</span>}</Label>
    <div className="mt-1.5">
      {f.type === "select" ? (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className="h-11 rounded-xl" data-testid={`field-${f.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      ) : f.type === "textarea" ? (
        <Textarea value={value} onChange={(e) => onChange(e.target.value)} className="rounded-xl" data-testid={`field-${f.key}`} />
      ) : (
        <Input type={f.type || "text"} value={value} onChange={(e) => onChange(e.target.value)} className="h-11 rounded-xl" data-testid={`field-${f.key}`} />
      )}
    </div>
  </div>
);

const ReviewBlock = ({ title, data, fields }) => {
  const labelFor = (k) => fields?.find((f) => f.key === k)?.label || k;
  const entries = Object.entries(data || {}).filter(([, v]) => v !== "" && v != null);
  if (entries.length === 0) return null;
  return (
    <div>
      <h4 className="font-head font-semibold text-emerald-950 mb-2 text-sm uppercase tracking-wide">{title}</h4>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
        {entries.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4 text-sm border-b border-dashed border-slate-100 py-1">
            <span className="text-slate-400">{labelFor(k)}</span>
            <span className="text-slate-700 text-right font-medium">{String(v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
